import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { EyeSlashIcon } from '@heroicons/react/24/outline';
import { getTournamentEvents, getTournaments, isValidUUID, supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Event, DayEvents } from '../types/content';

function Schedule() {
  const [events, setEvents] = useState<DayEvents[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPublished, setIsPublished] = useState(true);
  const [tournamentId, setTournamentId] = useState<string | null>(null);

  useEffect(() => {
    fetchTournamentAndEvents();
    checkSchedulePublishStatus();
  }, []);

  const checkSchedulePublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'schedule_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking schedule publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking schedule publish status:', err);
    }
  };

  const fetchTournamentAndEvents = async () => {
    try {
      // First, get the latest upcoming tournament
      const { data: tournaments, error: tournamentError } = await getTournaments();
      
      if (tournamentError) throw tournamentError;
      
      // Find the first upcoming tournament, or use the most recent one
      const upcomingTournament = tournaments?.find(t => t.status === 'upcoming');
      const targetTournament = upcomingTournament || tournaments?.[0];
      
      if (targetTournament?.id) {
        setTournamentId(targetTournament.id);
        await fetchEvents(targetTournament.id);
      } else {
        // If no tournaments found, use default events
        setDefaultEvents();
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching tournament:', err);
      toast.error('Failed to load tournament data');
      setDefaultEvents();
      setLoading(false);
    }
  };

  const fetchEvents = async (id: string) => {
    try {
      setLoading(true);
      
      if (!isValidUUID(id)) {
        throw new Error('Invalid tournament ID format');
      }
      
      const { data, error } = await getTournamentEvents(id);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Group events by date
        const eventsByDate = data.reduce((acc: Record<string, Event[]>, event) => {
          const date = event.date;
          if (!acc[date]) {
            acc[date] = [];
          }
          
          acc[date].push({
            id: event.id,
            date: event.date,
            time: event.time,
            title: event.title,
            location: event.location,
            description: event.description || ''
          });
          
          return acc;
        }, {});
        
        // Convert to array format needed by the component
        const formattedEvents: DayEvents[] = Object.entries(eventsByDate).map(([date, dayEvents], index) => ({
          id: index + 1,
          date,
          events: dayEvents.sort((a, b) => a.time.localeCompare(b.time))
        }));
        
        setEvents(formattedEvents);
      } else {
        // Use default events if no data
        setDefaultEvents();
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      toast.error('Failed to load events');
      setDefaultEvents();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultEvents = () => {
    setEvents([
      {
        id: 1,
        date: '2025-06-05',
        events: [
          {
            id: 'practice-1',
            date: '2025-06-05',
            time: '09:00',
            title: 'Practice Round',
            location: 'Course',
            description: 'Practice round for registered players'
          },
          {
            id: 'welcome-1',
            date: '2025-06-05',
            time: '18:00',
            title: 'Welcome Reception',
            location: 'Clubhouse',
            description: 'Welcome reception for players and officials'
          }
        ]
      },
      {
        id: 2,
        date: '2025-06-06',
        events: [
          {
            id: 'pro-am-1',
            date: '2025-06-06',
            time: '10:00',
            title: 'Pro-Am Tournament',
            location: 'Course',
            description: 'Pro-Am tournament featuring professionals and amateurs'
          },
          {
            id: 'briefing-1',
            date: '2025-06-06',
            time: '17:00',
            title: 'Players Briefing',
            location: 'Conference Hall',
            description: 'Official tournament briefing for all players'
          }
        ]
      },
      {
        id: 3,
        date: '2025-06-07',
        events: [
          {
            id: 'reg-1',
            date: '2025-06-07',
            time: '07:00',
            title: 'Registration & Warm-up',
            location: 'Clubhouse',
            description: 'Player registration and practice facilities open'
          },
          {
            id: 'round-1',
            date: '2025-06-07',
            time: '08:30',
            title: 'Round 1 Tee-off',
            location: 'Hole 1',
            description: 'First round of tournament play begins'
          }
        ]
      },
      {
        id: 4,
        date: '2025-06-08',
        events: [
          {
            id: 'round-2',
            date: '2025-06-08',
            time: '08:00',
            title: 'Round 2 Tee-off',
            location: 'Hole 1',
            description: 'Final round of tournament play'
          },
          {
            id: 'awards-2',
            date: '2025-06-08',
            time: '18:00',
            title: 'Awards Ceremony',
            location: 'Grand Ballroom',
            description: 'Celebration and presentation of awards'
          }
        ]
      }
    ]);
  };

  const formatDateForCalendar = (date: string, time: string) => {
    if (!date || !time) {
      return null;
    }

    const [hours = '00', minutes = '00'] = time.split(':');
    const paddedHours = hours.padStart(2, '0');
    const paddedMinutes = minutes.padStart(2, '0');
    const endHours = (parseInt(paddedHours) + 2).toString().padStart(2, '0');
    
    return {
      startDate: date,
      startTime: `${paddedHours}:${paddedMinutes}`,
      endTime: `${endHours}:${paddedMinutes}`
    };
  };

  const addToCalendar = (event: Event, dayDate: string) => {
    try {
      const formattedDate = formatDateForCalendar(dayDate, event.time);
      if (!formattedDate) {
        return null;
      }

      return (
        <AddToCalendarButton
          name={event.title}
          description={event.description}
          location={event.location}
          startDate={formattedDate.startDate}
          endDate={formattedDate.startDate}
          startTime={formattedDate.startTime}
          endTime={formattedDate.endTime}
          timeZone="Africa/Lagos"
          options={['Google', 'Apple', 'Microsoft365', 'iCal']}
          buttonStyle="text"
          styleLight="--btn-background: transparent; --btn-text: #FF5722; --btn-border: #FF5722;"
          label="Add to Calendar"
        />
      );
    } catch (error) {
      console.error('Error creating calendar event:', error);
      return null;
    }
  };

  const addAllDayToCalendar = (day: DayEvents) => {
    try {
      if (!day.date || !day.events?.length) {
        return null;
      }

      const firstEvent = day.events[0];
      const lastEvent = day.events[day.events.length - 1];
      
      if (!firstEvent || !lastEvent) {
        return null;
      }
      
      const firstTime = formatDateForCalendar(day.date, firstEvent.time);
      const lastTime = formatDateForCalendar(day.date, lastEvent.time);
      
      if (!firstTime || !lastTime) {
        return null;
      }

      return (
        <AddToCalendarButton
          name={`Ibom Open - ${format(parseISO(day.date), 'MMMM d, yyyy')} Events`}
          description={day.events.map((e) => `${e.time} - ${e.title}`).join('\n')}
          location="Ibom Golf & Country Club"
          startDate={day.date}
          endDate={day.date}
          startTime={firstTime.startTime}
          endTime={lastTime.endTime}
          timeZone="Africa/Lagos"
          options={['Google', 'Apple', 'Microsoft365', 'iCal']}
          buttonStyle="text"
          styleLight="--btn-background: transparent; --btn-text: #FF5722; --btn-border: #FF5722;"
          label="Add All Events to Calendar"
        />
      );
    } catch (error) {
      console.error('Error creating all-day calendar events:', error);
      return null;
    }
  };

  return (
    <div className="pt-20">
      {/* Unpublished Notice */}
      {!isPublished && (
        <div className="fixed top-24 inset-x-0 z-50 bg-yellow-100 border-b border-yellow-200 py-2">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-2 text-yellow-800">
              <EyeSlashIcon className="w-5 h-5" />
              <p className="text-sm font-medium">
                This page is currently unpublished and only visible to administrators
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-golf-gray text-white py-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-6xl font-display font-bold mb-6">
              Tournament Schedule
            </h1>
            <p className="text-xl text-golf-sand mb-8">
              Key events for the Ibom Open 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
              </div>
            ) : (
              events.map((day, dayIndex) => (
                <motion.div
                  key={day.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: dayIndex * 0.2 }}
                  className="mb-16 last:mb-0"
                >
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-3xl font-display font-bold text-golf-gray">
                      {format(parseISO(day.date), 'EEEE, MMMM d, yyyy')}
                    </h2>
                    <div className="flex items-center gap-4">
                      {addAllDayToCalendar(day)}
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {day.events.map((event) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-white rounded-xl shadow-md overflow-hidden"
                      >
                        <div className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-2">
                                <ClockIcon className="w-5 h-5 text-golf-orange" />
                                <span className="text-lg font-semibold">{event.time}</span>
                              </div>
                              <h3 className="text-xl font-bold text-golf-gray mb-2">{event.title}</h3>
                              <div className="flex items-center gap-2 text-gray-600 mb-2">
                                <MapPinIcon className="w-4 h-4" />
                                <span>{event.location}</span>
                              </div>
                              <p className="text-gray-600">{event.description}</p>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-3">
                              {addToCalendar(event, day.date)}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Schedule;